import * as rrwebModule from "rrweb"
import type { ReliaPrivacyOptions } from "../core/types"
import { EMAIL_REGEX, COMMON_USER_EMAIL } from "./collectors"

const CC_REGEX = /\b(?:\d[ -]*?){13,16}\b/g
const SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g

export interface ReplayRecorder {
  stop: () => void
  flush: () => void
}

/**
 * Max rrweb events per uploaded chunk. Every session — big or small — is
 * split into pieces of at most this size so the viewer can stream replays
 * progressively instead of waiting for one giant blob.
 */
export const MAX_EVENTS_PER_CHUNK = 50

/** Split an event batch into fixed-size pieces (pure, unit-tested). */
export function splitIntoChunks<T>(events: T[], size: number = MAX_EVENTS_PER_CHUNK): T[][] {
  if (size <= 0) return [events.slice()]
  const out: T[][] = []
  for (let i = 0; i < events.length; i += size) {
    out.push(events.slice(i, i + size))
  }
  return out.length > 0 ? out : []
}

// Resilient resolver for rrweb record function across ESM / CJS / Vite / Browser
function getRecordFn() {
  const mod = rrwebModule as any
  if (typeof mod.record === "function") return mod.record
  if (typeof mod.default?.record === "function") return mod.default.record
  if (typeof mod.default === "function") return mod.default
  if (typeof (window as any)?.rrweb?.record === "function") return (window as any).rrweb.record
  return null
}

export function startReplay(
  sink: (events: unknown[]) => void,
  chunkIntervalMs = 2500,
  privacy?: ReliaPrivacyOptions,
): ReplayRecorder | null {
  const record = getRecordFn()
  if (!record) {
    console.warn("[relia-sdk] rrweb record function not found in bundle.")
    return null
  }

  let queue: unknown[] = []
  let stopFn: (() => void) | null | undefined = null

  const flushQueue = () => {
    if (queue.length > 0) {
      const batch = queue.slice()
      queue = []
      // Always split into fixed-size pieces — every session uploads in
      // chunks, never as one giant blob, regardless of session size.
      for (const piece of splitIntoChunks(batch)) {
        try {
          sink(piece)
        } catch (err) {
          console.warn("[relia-sdk] Replay chunk sink error:", err)
        }
      }
    }
  }

  try {
    stopFn = record({
      emit(event: any, isCheckout?: boolean) {
        queue.push(event)
        // If full DOM snapshot (type 2) or checkout, immediately flush initial snapshot
        if (isCheckout || event?.type === 2) {
          setTimeout(flushQueue, 100)
        }
      },
      checkoutEveryNms: 60_000,
      inlineStylesheet: true,
      maskAllInputs: privacy?.maskAllInputs ?? true,
      maskTextClass: privacy?.maskClass || "relia-mask",
      blockClass: privacy?.blockClass || "relia-block",
      maskTextSelector:
        privacy?.maskTextSelector ||
        "input, textarea, select, [data-relia-mask], [data-relia-sensitive], .relia-mask, .relia-sensitive, [data-private], .sensitive",
      blockSelector: privacy?.blockSelector,
      maskInputOptions: {
        color: true,
        date: true,
        "datetime-local": true,
        email: true,
        month: true,
        number: true,
        password: true,
        range: true,
        search: true,
        tel: true,
        text: true,
        time: true,
        url: true,
        week: true,
        textarea: true,
        select: true,
        ...(privacy?.maskInputOptions || {}),
      },
      maskInputFn: (text: string) => {
        return text ? "•".repeat(Math.min(text.length, 10)) : ""
      },
      maskTextFn: (text: string) => {
        return text
          .replace(EMAIL_REGEX, COMMON_USER_EMAIL)
          .replace(CC_REGEX, "••••-••••-••••-••••")
          .replace(SSN_REGEX, "•••-••-••••")
      },
      sampling: {
        mousemove: true,
        mouseInteraction: true,
        scroll: 150,
        input: "last",
      },
    })
  } catch (err) {
    console.warn("[relia-sdk] Starting rrweb with standard options failed, trying fallback:", err)
    try {
      stopFn = record({
        emit(event: any) {
          queue.push(event)
          if (event?.type === 2) setTimeout(flushQueue, 100)
        },
      })
    } catch (fallbackErr) {
      console.error("[relia-sdk] Fatal error initializing rrweb:", fallbackErr)
      return null
    }
  }

  if (!stopFn) return null

  // Ensure initial snapshot is dispatched rapidly within 300ms
  const initialTimer = setTimeout(flushQueue, 300)
  const intervalTimer = setInterval(flushQueue, chunkIntervalMs)

  return {
    flush: flushQueue,
    stop: () => {
      clearTimeout(initialTimer)
      clearInterval(intervalTimer)
      flushQueue()
      try {
        stopFn?.()
      } catch {}
    },
  }
}
